import { createTheme } from '@mui/material';

/**
 * Тема журнала.
 *
 * MUI по умолчанию рисует синие кнопки и Roboto — в системе же оранжевый #FA7A45
 * и Montserrat. Раньше это выправлялось в каждом компоненте руками
 * (`color="warning"`, `textFieldStyle`, класс `filterSaveButton`), поэтому вместо
 * повторения тех же пропсов на каждом поле журнал получает тему целиком.
 *
 * `warning` намеренно совпадает с `primary`: в контингенте поля подписаны
 * `color="warning"`, и цвет должен быть тем же самым.
 */
const ORANGE = '#FA7A45';
const ORANGE_DARK = '#FF4B00';
const FONT = "'Montserrat', sans-serif";

// Оранжевый оставлен акцентом: кнопки и активная вкладка. Заголовки, содержимое таблиц
// и шапки — тёмные, иначе на экране не остаётся точки, за которую цепляется взгляд.
const DARK = '#2f2f2f';
const TEXT = '#3c3c3c';
const MUTED = '#8a8a8a';

export const journalTheme = createTheme({
  palette: {
    primary: { main: ORANGE, dark: ORANGE_DARK, contrastText: '#fff' },
    warning: { main: ORANGE, dark: ORANGE_DARK, contrastText: '#fff' },
    text: { primary: TEXT, secondary: MUTED },
  },

  typography: {
    fontFamily: FONT,
    fontSize: 13.5,
    h5: { fontFamily: FONT, fontSize: 22, fontWeight: 500, color: DARK },
    subtitle2: { fontFamily: FONT, fontSize: 15, fontWeight: 500, color: DARK },
    button: { fontFamily: FONT, fontWeight: 400, textTransform: 'none', letterSpacing: 0 },
  },

  components: {
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 2, fontSize: 14, padding: '4px 18px' },
        contained: { '&:hover': { backgroundColor: ORANGE_DARK } },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          fontFamily: FONT,
          fontSize: 15,
          fontWeight: 400,
          textTransform: 'none',
          letterSpacing: 0,
          color: '#8a8a8a',
          minHeight: 44,
          '&.Mui-selected': { color: ORANGE, fontWeight: 500 },
        },
      },
    },

    MuiTextField: { defaultProps: { size: 'small', color: 'warning' } },

    MuiInputBase: { styleOverrides: { root: { fontFamily: FONT, fontSize: 14 } } },
    MuiInputLabel: { styleOverrides: { root: { fontFamily: FONT, fontSize: 14 } } },
    MuiMenuItem: { styleOverrides: { root: { fontFamily: FONT, fontSize: 14 } } },
    MuiFormHelperText: { styleOverrides: { root: { fontFamily: FONT, fontSize: 11 } } },

    MuiTableCell: {
      styleOverrides: {
        root: { fontFamily: FONT, fontSize: 13.5, borderColor: '#e8e8e8', color: TEXT },
        head: { fontWeight: 500, color: DARK, backgroundColor: '#f4f4f4' },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontFamily: FONT, fontSize: 12, borderRadius: 3 },
      },
    },

    MuiPaper: { styleOverrides: { rounded: { borderRadius: 4 } } },

    MuiDialogTitle: {
      styleOverrides: {
        root: { fontFamily: FONT, fontSize: 18, fontWeight: 500, color: DARK },
      },
    },

    MuiTooltip: {
      styleOverrides: { tooltip: { fontFamily: FONT, fontSize: 12 } },
    },

    MuiAlert: { styleOverrides: { root: { fontFamily: FONT, fontSize: 13 } } },
  },
});

export const JOURNAL_COLORS = { ORANGE, ORANGE_DARK, DARK, TEXT, MUTED };
